import React, { useCallback, useMemo, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Hidden, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import {
  useActiveExteriorSignageQuote,
  useExteriorSignageQuote,
  usePatchApproveExteriorSignageQuote,
  usePatchExteriorSignageQuoteChangeInformation,
  usePostExteriorSignageQuoteCheckout,
} from 'services/Data/ExteriorSignageQuoteService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import ExteriorSignageQuoteInfoCheckoutSection from './ExteriorSignageQuoteInfoCheckoutSection';
import ExteriorSignageQuoteDeliveryCheckoutSection from './ExteriorSignageQuoteDeliveryCheckoutSection';
import { useHistory, useParams } from 'react-router-dom';
import BaseModal from 'Components/BaseModal';
import SimpleReactValidator from 'simple-react-validator';
import { validatorConfig } from 'services/Validators/Validation';
import useToast from 'services/Hooks/useToast';
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { useSelector } from 'react-redux';
import CustomInput from '@acnb/CustomComponents/CustomInput/CustomInput';
import { ExteriorSignageQuoteStatusEnumValue } from 'enums/ExteriorSignageQuoteStatusEnum';
import SkeletonWrapper from 'Components/SkeletonWrapper';
import ExteriorSignageQuoteTermConditions from './ExteriorSignageQuoteTermConditions';
import PhoneInput from '@acnb/CustomComponents/PhoneInput/PhoneInput';
import { homePage, IsClub, IsImpersonationMode } from 'config/config';
import StorePageHeader from 'Components/StorePageHeader';
import { ExteriorSignageQuoteAttachedFileTypeEnum } from 'enums/ExteriorSignageQuoteAttachedFileTypeEnum';
import { useActiveCart } from 'services/Data/CartService';
import { useActiveMerchandisingProgramQuote } from 'services/Data/MerchandisingProgramQuoteService';
import {
  buildPathWithBrpClubDealerSelectionTrigger,
  shouldAutoOpenBrpClubDealerSelection,
} from 'services/Utilities/dealerWorkUtils';

const useStyles = makeStyles(theme => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkoutBtn: {
    marginTop: '1vh',
  },
  deliverySection: {
    position: 'sticky',
    top: '10vh',
  },
  card: {
    overflow: 'visible',
  },
  checkboxError: {
    color: theme.palette.error.main,
    '& .MuiSvgIcon-root': {
      color: theme.palette.error.main,
    },
  },
}));

const ExteriorSignageQuoteCheckout = ({ fromHistory, fromProfile }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const forceUpdate = useForceUpdate();

  const { errorMessage } = useToast();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const [showCheckoutConfirmed, setShowCheckoutConfirmed] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [hasAttemptedCheckout, setHasAttemptedCheckout] = useState(false);
  const [agreements, setAgreements] = useState({
    siteVerification: false,
    noReimbursement: false,
    permitApplication: false,
    municipalCompliance: false,
    timelines: false,
    oemParity: false,
  });

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  let { exteriorSignageQuoteId } = useParams();

  // active cart is used in cart (it's the current cart being in use)
  // history cart is used in the history only
  const { data: activeExteriorSignageQuote, isLoading: gettingExteriorSignageQuote } = useActiveExteriorSignageQuote(
    accountReducer.isLoggedIn,
  );
  const { data: activeCart, isLoading: gettingActiveCart } = useActiveCart(
    accountReducer.isLoggedIn,
    false,
    IsImpersonationMode ? dealerId : null,
  );
  const {
    data: activeMerchandisingProgramQuote,
    isLoading: gettingActiveMerchandisingProgramQuote,
  } = useActiveMerchandisingProgramQuote(accountReducer.isLoggedIn);
  const { data: historyExteriorSignageQuote, isLoading: gettingExteriorSignageQuoteHistory } = useExteriorSignageQuote(
    exteriorSignageQuoteId,
  );
  const { mutate: changeInformations, isPending: changingInformations } = usePatchExteriorSignageQuoteChangeInformation();

  const exteriorSignageQuote = useMemo(
    () => (exteriorSignageQuoteId ? historyExteriorSignageQuote : activeExteriorSignageQuote),
    [exteriorSignageQuoteId, historyExteriorSignageQuote, activeExteriorSignageQuote],
  );

  const { mutate: exteriorSignageQuoteCheckout, isPending: isCheckingOut } = usePostExteriorSignageQuoteCheckout();
  const { mutate: changeExteriorSignageQuoteStatut } = usePatchApproveExteriorSignageQuote();

  const handleCheckout = useCallback(() => {
    setHasAttemptedCheckout(true);

    let hasOemError = false;
    let hasLogoError = false;
    const items = exteriorSignageQuote?.items || exteriorSignageQuote?.quoteItems || [];

    const legacyFiles = exteriorSignageQuote?.files?.filter(f => f.exteriorSignageQuoteItem_Id == null) || [];
    const isLegacyQuote = legacyFiles.length > 0;

    items.forEach(item => {
      const requiredOem = isLegacyQuote ? 0 : item.allowOEM && item.quantityOEM > 0 ? item.quantityOEM * item.quantity : 0;
      const requiredLogo = isLegacyQuote ? 0 : item.allowDealerLogo ? 1 : 0;

      const uploadedOem =
        exteriorSignageQuote?.files?.filter(
          f =>
            f.exteriorSignageQuoteType === ExteriorSignageQuoteAttachedFileTypeEnum.OEM &&
            f.exteriorSignageQuoteItem_Id === item.id,
        ).length || 0;

      const uploadedLogo = isLegacyQuote
        ? legacyFiles.length
        : exteriorSignageQuote?.files?.filter(
            f =>
              f.exteriorSignageQuoteType === ExteriorSignageQuoteAttachedFileTypeEnum.Logo &&
              f.exteriorSignageQuoteItem_Id === item.id,
          ).length || 0;

      if (requiredOem > 0 && uploadedOem !== requiredOem) hasOemError = true;
      if (requiredLogo > 0 && uploadedLogo === 0) hasLogoError = true;
    });

    if (!validator.current.allValid() || hasOemError || hasLogoError) {
      validator.current.showMessages();
      forceUpdate();

      errorMessage({
        message: 'contraint_errors',
      });
      return;
    }

    validator.current.hideMessages();

    exteriorSignageQuoteCheckout({
      id: exteriorSignageQuote.id,
      areTermsAccepted: true,
    });
    setShowCheckoutConfirmed(true);
  }, [errorMessage, exteriorSignageQuote, exteriorSignageQuoteCheckout, forceUpdate]);

  const handleCloseCancelExteriorSignageQuoteModal = useCallback(() => {
    setShowCancelModal(false);
  }, [setShowCancelModal]);

  const handleCloseConfirmedCheckoutModal = useCallback(() => {
    setShowCheckoutConfirmed(false);
  }, [setShowCheckoutConfirmed]);

  const handleChangeStatus = useCallback(
    newStatus => {
      changeExteriorSignageQuoteStatut({ ...exteriorSignageQuote, status: newStatus });
    },
    [changeExteriorSignageQuoteStatut, exteriorSignageQuote],
  );

  const isLoading = useMemo(
    () => gettingExteriorSignageQuote || gettingExteriorSignageQuoteHistory || isCheckingOut || changingInformations,
    [changingInformations, gettingExteriorSignageQuote, gettingExteriorSignageQuoteHistory, isCheckingOut],
  );

  const actionButtons = useMemo(
    () => [
      {
        variant: 'contained',
        color: 'secondary',
        hidden: exteriorSignageQuoteId,
        action:
          !exteriorSignageQuoteId &&
          exteriorSignageQuote?.status === ExteriorSignageQuoteStatusEnumValue.Active &&
          handleCheckout,
        label: !exteriorSignageQuoteId && t('proceedCheckoutExteriorSignageQuote'),
        disabled:
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active || exteriorSignageQuote?.isDemo,
      },
      {
        variant: 'contained',
        color: 'error',
        hidden: !exteriorSignageQuoteId,
        action: () => setShowCancelModal(true),
        label: t('cancel'),
        disabled:
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Submitted &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.PendingQuote &&
          exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.PendingDealerApproval,
      },
    ],
    [exteriorSignageQuote?.status, exteriorSignageQuoteId, handleCheckout, t, exteriorSignageQuote?.isDemo],
  );

  const backUrl = useMemo(() => (fromHistory ? '/dealer/profile/request-histories' : '/dealer/profile/awaiting-requests'), [
    fromHistory,
  ]);
  const shouldAutoOpenDealerSelection = useMemo(
    () =>
      shouldAutoOpenBrpClubDealerSelection({
        isClub: IsClub,
        isLoading: gettingActiveCart || gettingActiveMerchandisingProgramQuote || gettingExteriorSignageQuote,
        cart: activeCart,
        merchandisingProgramQuote: activeMerchandisingProgramQuote,
        exteriorSignageQuote: activeExteriorSignageQuote,
      }),
    [
      activeCart,
      activeExteriorSignageQuote,
      activeMerchandisingProgramQuote,
      gettingActiveCart,
      gettingActiveMerchandisingProgramQuote,
      gettingExteriorSignageQuote,
    ],
  );
  const confirmedCheckoutRedirectPath = useMemo(
    () => (shouldAutoOpenDealerSelection ? buildPathWithBrpClubDealerSelectionTrigger(homePage) : homePage),
    [shouldAutoOpenDealerSelection],
  );

  return (
    <Box className={classes.pageFull}>
      <BaseModal
        show={showCheckoutConfirmed}
        title={t('exteriorSignageQuoteCheckoutConfirmed')}
        message={t('exteriorSignageQuoteCheckoutConfirmedText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseConfirmedCheckoutModal}
        actions={[
          {
            onClick: () => history.push(confirmedCheckoutRedirectPath),
            color: 'secondary',
            style: { color: 'black', borderRadius: 0 },
            variant: 'contained',
            message: t('goToHome'),
          },
        ]}
      />
      <BaseModal
        show={showCancelModal}
        title={t('cancelExteriorSignageQuote')}
        message={t('cancelExterirorSignageQuoteText')}
        closeBtnMessage={t('close')}
        onClose={handleCloseCancelExteriorSignageQuoteModal}
        actions={[
          {
            onClick: () => {
              handleChangeStatus(ExteriorSignageQuoteStatusEnumValue.Cancelled);
              history.push(backUrl);
            },
            color: 'error',
            style: { color: 'white' },
            variant: 'contained',
            message: t('cancel'),
          },
        ]}
      />
      {!fromProfile && (
        <Box sx={{ mb: 9 }}>
          <StorePageHeader title={t('quote')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
        </Box>
      )}

      <CmtCard className={classes.card}>
        <CmtCardHeader title={''}>
          {exteriorSignageQuoteId && (
            <Button
              onClick={() => history.push(backUrl)}
              variant="outlined"
              sx={{
                borderRadius: 0,
              }}>
              {t('backToList')}
            </Button>
          )}
        </CmtCardHeader>
        <CmtCardContent>
          <GridContainer>
            <GridItem xs={12}>
              <Typography variant="h1">{t('contactUs')}</Typography>
              <Box sx={{ my: 5 }}>
                <Box sx={{ my: 1 }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('exteriorSignageQuoteContactUsBody1', { email: 'enseignes.exterieures@brp.com' }),
                    }}
                  />
                </Box>
                <Box sx={{ my: 1 }}>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('exteriorSignageQuoteContactUsBody2'),
                    }}
                  />
                </Box>
              </Box>
            </GridItem>
            <GridItem xs={12} md={6}>
              <Box className={classes.deliverySection}>
                <Typography variant="h1">{t('contactInformation')}</Typography>
                <Box sx={{ my: 5 }}>
                  <GridContainer>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('contactName')}
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('contactName'),
                                exteriorSignageQuote?.contactName,
                                `required`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...exteriorSignageQuote, contactName: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactName || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('email')}
                              id="email"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('email'),
                                exteriorSignageQuote?.contactEmail,
                                `required|email`,
                              )}
                              inputProps={{
                                onBlur: ({ target: { value } }) => {
                                  changeInformations({ ...exteriorSignageQuote, contactEmail: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactEmail || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                    <GridItem xs={12}>
                      <SkeletonWrapper
                        skeletonProps={{
                          width: 210,
                          height: 33,
                        }}
                        isLoading={gettingExteriorSignageQuote}
                        component={
                          exteriorSignageQuote && (
                            <CustomInput
                              labelText={t('phone')}
                              labelProps={{
                                shrink: isPhoneFocused || exteriorSignageQuote.contactPhoneNumber,
                              }}
                              id="phone"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              error={validator.current.message(
                                t('phone'),
                                exteriorSignageQuote?.contactPhoneNumber,
                                `required`,
                              )}
                              inputProps={{
                                inputComponent: PhoneInput,
                                onFocus: () => setIsPhoneFocused(true),
                                onBlur: ({ target: { value } }) => {
                                  setIsPhoneFocused(false);
                                  changeInformations({ ...exteriorSignageQuote, contactPhoneNumber: value });
                                },
                                disabled: exteriorSignageQuoteId,
                                defaultValue: exteriorSignageQuote.contactPhoneNumber || '',
                              }}
                            />
                          )
                        }
                      />
                    </GridItem>
                  </GridContainer>
                </Box>

                <Typography variant="h1">{t('delivery')}</Typography>
                <ExteriorSignageQuoteDeliveryCheckoutSection
                  exteriorSignageQuote={exteriorSignageQuote}
                  isLoading={isLoading}
                  validator={validator}
                  isDisabled={exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active}
                  actionBtns={actionButtons}
                  agreements={agreements}
                  setAgreements={setAgreements}
                />
              </Box>
            </GridItem>
            <GridItem xs={12} md={6}>
              <Box>
                <Typography variant="h1">{t('quoteInfo')}</Typography>
                <ExteriorSignageQuoteInfoCheckoutSection
                  validator={validator}
                  exteriorSignageQuote={exteriorSignageQuote}
                  isLoading={isLoading}
                  isDisabled={exteriorSignageQuoteId}
                  hasAttemptedCheckout={hasAttemptedCheckout}
                />
              </Box>
            </GridItem>

            <Hidden mdUp>
              <GridItem xs={12}>
                <ExteriorSignageQuoteTermConditions
                  exteriorSignageQuote={exteriorSignageQuote}
                  isDisabled={exteriorSignageQuote?.status !== ExteriorSignageQuoteStatusEnumValue.Active}
                  validator={validator}
                  agreements={agreements}
                  setAgreements={setAgreements}
                />
              </GridItem>
              <GridItem xs={12}>
                {actionButtons
                  .filter(b => !b.hidden)
                  .map(btnInfo => (
                    <Button
                      key={btnInfo.label}
                      className={classes.checkoutBtn}
                      sx={{ borderRadius: 0 }}
                      variant={btnInfo.variant}
                      color={btnInfo.color}
                      disabled={btnInfo.disabled}
                      onClick={() => btnInfo.action()}>
                      {btnInfo.label}
                    </Button>
                  ))}
              </GridItem>
            </Hidden>
          </GridContainer>
        </CmtCardContent>
      </CmtCard>
    </Box>
  );
};

export default ExteriorSignageQuoteCheckout;
